<ng-template #filterItemLabel let-filter="filter">
  <span data-id="filter-tag-label" class="bd-label-regular flex">
    @for (label of filter.label; track label.text) {
      <span class="flex items-center">
        @if (label.icon) {
          <i
            data-id="filter-tag-label-icon"
            class="{{ label.icon }}"
            [ngClass]="$first ? 'mr-2' : 'mx-2'"
          ></i>
        } @else if (!$first) {
          <span>&nbsp;</span>
        }
        @let text = (label | translateDisplayText: 'text' | async) ?? '';
        <span data-id="filter-tag-label-text">{{ text }}</span>
        @if (!$last) {
          <span>&nbsp;|</span>
        }
      </span>
    }
  </span>
</ng-template>

<p-iconfield
  class="w-full h-full"
  iconPosition="right"
  data-id="searchbar"
  *transloco="let t; scope: 'featureSearch'; prefix: 'featureSearch.searchbar'"
>
  <p-autocomplete
    #autocomplete
    data-id="searchbar-input"
    placeholder="{{
      aiSearchToggleOn() ? t('aiPlaceholder') : t('placeholder')
    }}"
    styleClass="w-full h-full {{
      visibleSuggestions.length > 0 ? '' : 'bd-hide-typeahead-results'
    }} bd-global-search"
    [(ngModel)]="searchTerm"
    (input)="onInputChange($event)"
    [delay]="0"
    [suggestions]="visibleSuggestions"
    [disabled]="disabled"
    [showEmptyMessage]="false"
    [showClear]="false"
    (onClear)="clearInput(false)"
    (keydown.enter)="onInputEnterKeyDown($event)"
    inputStyleClass="bd-global-search-input {{
      aiSearchEnabled() ? 'bd-ai-search-enabled' : ''
    }}"
    [group]="true"
    optionLabel="name"
    optionValue="name"
    (onHide)="onAutoCompleteHide()"
    (completeMethod)="search($event)"
    [scrollHeight]="context === 'search' ? 'calc(100vh - 80px)' : 'unset'"
    (onSelect)="onSuggestionSelect($event.value)"
    (onFocus)="onInputFocus()"
    (click)="onInputClick()"
    [typeahead]="true"
  >
    <ng-template let-suggestion #item>
      @if (suggestion.type === 'filter') {
        <div
          data-id="filter-suggestions"
          class="flex flex-wrap gap-4 bd-suggestion-filters"
        >
          @for (
            filterSuggestion of suggestion.appliedFilters;
            track filterSuggestion
          ) {
            <p-button
              class="bd-suggestion-filter"
              [attr.data-id]="
                'selected-suggestion-filter-' + filterSuggestion.id
              "
              styleClass="bd-filter-tag bd-filter-tag-small"
              (onClick)="
                onFilterSuggestionClick(filterSuggestion, $event, false);
                autocomplete.show()
              "
            >
              <div class="bd-tag-icon">
                <span class="ui-check"> </span>
                <span class="ui-close"> </span>
              </div>
              <ng-container
                *ngTemplateOutlet="
                  filterItemLabel;
                  context: { filter: filterSuggestion }
                "
              ></ng-container>
            </p-button>
          }
          @for (
            filterSuggestion of suggestion.filters;
            track filterSuggestion
          ) {
            <p-button
              class="bd-body-regular bd-suggestion-filter"
              [attr.data-id]="'suggestion-filter-' + filterSuggestion.id"
              styleClass="bd-filter-tag bd-filter-tag-small"
              [outlined]="true"
              (onClick)="
                onFilterSuggestionClick(filterSuggestion, $event, true);
                autocomplete.show()
              "
            >
              <ng-container
                *ngTemplateOutlet="
                  filterItemLabel;
                  context: { filter: filterSuggestion }
                "
              ></ng-container>
            </p-button>
          }
        </div>
      } @else if (suggestion.type === 'query') {
        @let query = (suggestion | translateDisplayText: 'query' | async) ?? '';
        <div [attr.data-id]="'suggestion-query-' + query" class="flex gap-3">
          <div class="bd-suggestion-item-icon-wrapper">
            <span
              [attr.data-id]="'suggestion-query-icon-' + query"
              class="ui-search bd-smart-suggestion-icon"
            ></span>
          </div>
          <span
            class="bd-body-regular bd-smart-suggestion"
            [attr.data-id]="'suggestion-query-' + query"
            [innerHTML]="query | highlight: searchTerm : true | bdSanitize"
          >
          </span>
        </div>
      } @else {
        <div
          [attr.data-id]="'suggestion-result-' + suggestion.name"
          class="flex gap-3"
        >
          <div class="bd-suggestion-item-icon-wrapper">
            <span
              [attr.data-id]="'suggestion-result-icon-' + suggestion.name"
              class="{{ suggestion.icon }}"
            ></span>
          </div>
          <span
            class="bd-body-regular"
            [attr.data-id]="'suggestion-result-name-' + suggestion.name"
            [innerHTML]="suggestion.name | highlight: searchTerm"
          >
          </span>
        </div>
      }
    </ng-template>
    <ng-template let-group #group>
      @let label = (group | translateDisplayText: 'label' | async) ?? '';
      <div [attr.data-id]="'suggestions-group-' + label">
        <p-divider class="bd-suggestions-group"></p-divider>
        <div
          [attr.data-id]="'suggestions-group-label-' + label"
          class="bd-group-label"
        >
          {{ label }}
        </div>
        @if (group.loading) {
          @for (n of [].constructor(4); track $index) {
            <div class="flex bd-suggestion-skeleton">
              <p-skeleton width="16px" />
              <p-skeleton width="206px" />
            </div>
          }
        }
      </div>
    </ng-template>
    <ng-template #footer>
      @if (!aiSearchToggleOn() && searchTerm) {
        <div #seeAllButton class="bd-footer">
          <p-button
            data-id="see-all-suggestions-button"
            [label]="t('seeAll')"
            [link]="true"
            styleClass="bd-no-capitalize"
            (onClick)="onSearchButtonClick($event)"
          ></p-button>
        </div>
      }
    </ng-template>
  </p-autocomplete>
  <p-inputicon>
    <div
      class="flex items-center h-full gap-2"
      (mousedown)="onControlsContainerMouseDown($event)"
    >
      @if (searchTerm) {
        <p-button
          styleClass="bd-neutral bd-size-small"
          data-id="searchbar-clear-button"
          icon="ui-close"
          [text]="true"
          (onClick)="clearInput()"
        >
        </p-button>
      }
      @if (aiSearchEnabled()) {
        <label data-id="searchbar-ai-toggle-switch" class="bd-toggle-switch">
          <input
            type="checkbox"
            [checked]="toggleState()"
            [attr.aria-label]="t('aiToggle')"
            [ngClass]="{ 'bd-toggle-on': toggleState() }"
            (change)="onToggle()"
          />
          <span class="slider" [ngClass]="{ 'bd-toggle-on': toggleState() }">
            <span
              class="bd-slider-icon ui-stars"
              [ngClass]="{ 'bd-toggle-on': toggleState() }"
              aria-hidden="true"
            ></span>
            <span class="bd-slider-on bd-button-regular">{{ t('aiOn') }}</span>
            <span class="bd-slider-off bd-button-regular">{{
              t('aiOff')
            }}</span>
          </span>
        </label>
      }
      <p-button
        styleClass="bd-size-small"
        data-id="searchbar-button"
        icon="ui-search"
        [disabled]="disabled"
        (mousedown)="onSearchButtonMouseDown($event)"
        (onClick)="onSearchButtonClick($event)"
      >
      </p-button>
    </div>
  </p-inputicon>
</p-iconfield>
